import FilterBar from "@/components/base/FilterBar";
import Pagination from "@/components/base/Pagination";
import UserIdentityCell from "@/components/base/UserIdentityCell";
import { glopicApi } from "@/lib/glopic-api";
import { formatDateTime } from "@/lib/format";
import { buildOrderFilters, safeOrderFailureText } from "./contracts";

interface OrderRow {
  order_id: string;
  user_id: string;
  user_type: "consumer" | "merchant";
  nickname: string | null;
  avatar: string | null;
  package_id: string | null;
  product_type: "vip_subscription" | "merchant_points";
  merchant_plan_id: string | null;
  pay_channel: string;
  amount: number;
  currency: string;
  status: number;
  fulfillment_status: "pending" | "fulfilled" | "failed";
  points_amount: number;
  points_granted: number;
  failure_code: string | null;
  paid_at: string | null;
  fulfilled_at: string | null;
  created_at: string;
}

const statusLabels: Record<number, string> = {
  0: "待支付",
  1: "已支付",
  2: "支付失败",
  3: "已取消",
  4: "已退款",
};

const fulfillmentLabels = {
  pending: "待履约",
  fulfilled: "已履约",
  failed: "履约失败",
};

function downloadBlob(data: string | Blob | ArrayBuffer, filename: string) {
  const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function productLabel(order: OrderRow) {
  if (order.product_type === "merchant_points") {
    return `商家积分包 · ${order.merchant_plan_id || "-"}`;
  }
  return `VIP 订阅 · ${order.package_id || "-"}`;
}

export default function AiOrdersPage() {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState("");
  const [userType, setUserType] = useState("");
  const [productType, setProductType] = useState("");
  const [fulfillmentStatus, setFulfillmentStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const filters = useCallback(
    () => buildOrderFilters({
      userId,
      status,
      userType,
      productType,
      fulfillmentStatus,
    }),
    [fulfillmentStatus, productType, status, userId, userType],
  );

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await glopicApi.get("/orders", {
        params: { ...filters(), page, page_size: pageSize },
      });
      setOrders(response.data.data.items ?? []);
      setTotal(response.data.data.total ?? 0);
    } catch (error) {
      console.error("Fetch orders error:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  const exportOrders = async () => {
    setExporting(true);
    try {
      const response = await glopicApi.get("/orders/export", {
        params: filters(),
        responseType: "blob",
      });
      downloadBlob(response.data, `glowpic-orders-${userType || "all"}.csv`);
    } catch (error) {
      console.error("Export orders error:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">全部订单</h2>
          <p className="mt-1 text-sm text-text-secondary">
            统一治理 VIP 订阅与商家积分包订单，共 {total} 笔
          </p>
        </div>
        <button
          onClick={() => void exportOrders()}
          disabled={exporting}
          className="h-9 rounded-lg border border-surface-border px-3 text-sm text-text-secondary disabled:opacity-50"
        >
          {exporting ? "导出中..." : "导出当前筛选"}
        </button>
      </div>

      <FilterBar
        search={userId}
        onSearchChange={(value) => {
          setUserId(value);
          setPage(1);
        }}
        searchPlaceholder="输入用户 ID..."
        filters={[
          {
            key: "status",
            label: "全部支付状态",
            type: "select",
            options: Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
          },
          {
            key: "user_type",
            label: "全部用户类型",
            type: "select",
            options: [
              { value: "consumer", label: "C端用户" },
              { value: "merchant", label: "商家用户" },
            ],
          },
          {
            key: "product_type",
            label: "全部商品类型",
            type: "select",
            options: [
              { value: "vip_subscription", label: "VIP订阅" },
              { value: "merchant_points", label: "商家积分包" },
            ],
          },
          {
            key: "fulfillment_status",
            label: "全部履约状态",
            type: "select",
            options: Object.entries(fulfillmentLabels).map(
              ([value, label]) => ({ value, label }),
            ),
          },
        ]}
        filterValues={{
          status,
          user_type: userType,
          product_type: productType,
          fulfillment_status: fulfillmentStatus,
        }}
        onFilterChange={(key, value) => {
          if (key === "status") setStatus(value);
          if (key === "user_type") setUserType(value);
          if (key === "product_type") setProductType(value);
          if (key === "fulfillment_status") setFulfillmentStatus(value);
          setPage(1);
        }}
        onReset={() => {
          setUserId("");
          setStatus("");
          setUserType("");
          setProductType("");
          setFulfillmentStatus("");
          setPage(1);
        }}
      />

      <div className="overflow-hidden rounded-xl border border-surface-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1220px] text-sm">
            <thead className="border-b border-surface-border bg-surface/50 text-xs text-text-secondary">
              <tr>
                <th className="px-4 py-3 text-left">订单 / 用户</th>
                <th className="px-4 py-3 text-left">商品</th>
                <th className="px-4 py-3 text-left">金额 / 渠道</th>
                <th className="px-4 py-3 text-left">支付状态</th>
                <th className="px-4 py-3 text-left">履约状态</th>
                <th className="px-4 py-3 text-left">积分</th>
                <th className="px-4 py-3 text-left">时间</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                    加载中...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-text-muted">
                    暂无订单
                  </td>
                </tr>
              ) : orders.map((order) => (
                <tr key={order.order_id} className="border-b border-surface-border">
                  <td className="px-4 py-4">
                    <p className="font-mono text-xs text-text-primary">{order.order_id}</p>
                    <UserIdentityCell
                      nickname={order.nickname}
                      userId={order.user_id}
                      avatar={order.avatar}
                    />
                    <p className="text-[10px] text-text-muted">
                      {order.user_type === "merchant" ? "商家用户" : "C端用户"}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-xs text-text-secondary">
                    {productLabel(order)}
                  </td>
                  <td className="px-4 py-4">
                    <p>{Number(order.amount).toFixed(2)} {order.currency}</p>
                    <p className="text-xs text-text-muted">{order.pay_channel}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p>{statusLabels[order.status] || order.status}</p>
                    <p className="text-xs text-red-600">
                      {safeOrderFailureText(order.failure_code)}
                    </p>
                  </td>
                  <td className="px-4 py-4">{fulfillmentLabels[order.fulfillment_status]}</td>
                  <td className="px-4 py-4 text-xs">
                    应发 {order.points_amount}<br />已发 {order.points_granted}
                  </td>
                  <td className="px-4 py-4 text-xs text-text-muted">
                    下单 {formatDateTime(order.created_at)}<br />
                    履约 {formatDateTime(order.fulfilled_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {Math.ceil(total / pageSize) > 1 && (
          <Pagination
            page={page}
            totalPages={Math.ceil(total / pageSize)}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(value) => {
              setPageSize(value);
              setPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
